/**
 * batch-62 garage-voertuig (FO-462 + FO-465) - gedeelde types, Zod-schema's en
 * DTO's.
 *
 *   FO-462  onderhoud vs schade/reparatie: kostencategorie per garagefactuur-
 *           regel (automatisch voorstel, corrigeerbaar met reden + audit),
 *           schade gekoppeld aan voertuig + berijder op de schadedatum + het
 *           schadedossier (= SchadeMelding, CC.0b), inzicht per voertuig en per
 *           medewerker (gevoelig), export per periode.
 *   FO-465  afspraak-keten (drie ingangen, documenten bij het afronden, volgende
 *           actie met beheerbaar interval per soort), km-plausibiliteit met
 *           review i.p.v. overschrijven, review-lijst met de factuur ernaast.
 *
 * Eén request-schema per mutatie, gedeeld door de API (`safeParse` ->
 * `sendValidationError`) en het formulier (client-side dezelfde `safeParse`).
 */

import { z } from 'zod'
import type { OnderhoudRegelClassificatie } from './garage-onderhoud'
import {
  afspraakRedenSchema,
  VOERTUIG_AFSPRAAK_SOORT,
  type VoertuigAfspraakSoort,
  type VoertuigAfspraakStatus,
} from './voertuig-afspraken'

// ─── Enums (spiegel van schema.prisma) ───────────────────────────────────────

export const GARAGE_KOSTEN_CATEGORIE = ['ONDERHOUD', 'SCHADE_REPARATIE'] as const
export type GarageKostenCategorie = (typeof GARAGE_KOSTEN_CATEGORIE)[number]

export const GARAGE_KOSTEN_CATEGORIE_LABELS: Record<GarageKostenCategorie, string> = {
  ONDERHOUD: 'Onderhoud',
  SCHADE_REPARATIE: 'Schade/reparatie',
}

/**
 * Weergave-bakken voor totalen: de twee categorieën plus de regels waarvan de
 * keuze nog openstaat. Een bedrag verdwijnt nooit stil in een van beide.
 */
export const GARAGE_KOSTEN_BAK = ['ONDERHOUD', 'SCHADE_REPARATIE', 'NOG_IN_TE_DELEN'] as const
export type GarageKostenBak = (typeof GARAGE_KOSTEN_BAK)[number]

export const GARAGE_KOSTEN_BAK_LABELS: Record<GarageKostenBak, string> = {
  ONDERHOUD: 'Onderhoud',
  SCHADE_REPARATIE: 'Schade/reparatie',
  NOG_IN_TE_DELEN: 'Nog in te delen',
}

export const GARAGE_KOSTEN_CATEGORIE_BRON = ['AUTOMATISCH', 'HANDMATIG'] as const
export type GarageKostenCategorieBron = (typeof GARAGE_KOSTEN_CATEGORIE_BRON)[number]

export const GARAGE_SCHADE_DATUM_BRON = [
  'SCHADEMELDING',
  'WERKDATUM',
  'FACTUURDATUM',
  'HANDMATIG',
] as const
export type GarageSchadeDatumBron = (typeof GARAGE_SCHADE_DATUM_BRON)[number]

export const GARAGE_SCHADE_DATUM_BRON_LABELS: Record<GarageSchadeDatumBron, string> = {
  SCHADEMELDING: 'uit de schademelding',
  WERKDATUM: 'werkdatum op de factuur',
  FACTUURDATUM: 'factuurdatum (geen werkdatum vermeld)',
  HANDMATIG: 'handmatig vastgesteld',
}

export const GARAGE_SCHADE_TOEWIJZING = ['AUTOMATISCH', 'HANDMATIG', 'NIET_TOEWIJSBAAR'] as const
export type GarageSchadeToewijzing = (typeof GARAGE_SCHADE_TOEWIJZING)[number]

export const GARAGE_SCHADE_TOEWIJZING_LABELS: Record<GarageSchadeToewijzing, string> = {
  AUTOMATISCH: 'Berijder op de schadedatum',
  HANDMATIG: 'Handmatig gekozen',
  NIET_TOEWIJSBAAR: 'Niet toewijsbaar',
}

export const SCHADE_MELDING_BRON = ['FORMULIER', 'GARAGEFACTUUR'] as const
export type SchadeMeldingBron = (typeof SCHADE_MELDING_BRON)[number]

export const SCHADE_MELDING_BRON_LABELS: Record<SchadeMeldingBron, string> = {
  FORMULIER: 'Aanrijdingsformulier',
  GARAGEFACTUUR: 'Uit garagefactuur (geen formulier)',
}

export const GARAGE_REVIEW_SOORT = [
  'CATEGORIE_KEUZE',
  'SCHADE_TOEWIJZING',
  'KM_LAGER',
  'KM_ONREALISTISCH',
  'KM_ONTBREEKT',
  'DATUM_AFWIJKING',
] as const
export type GarageReviewSoort = (typeof GARAGE_REVIEW_SOORT)[number]

export const GARAGE_REVIEW_SOORT_LABELS: Record<GarageReviewSoort, string> = {
  CATEGORIE_KEUZE: 'Onderhoud of schade: keuze nodig',
  SCHADE_TOEWIJZING: 'Schade: berijder en dossier bevestigen',
  KM_LAGER: 'Kilometerstand lager dan de vorige stand',
  KM_ONREALISTISCH: 'Kilometerstand onrealistisch',
  KM_ONTBREEKT: 'Geen kilometerstand op de factuur',
  DATUM_AFWIJKING: 'Afwijkende werkdatum',
}

export const GARAGE_REVIEW_STATUS = ['OPEN', 'AFGEHANDELD'] as const
export type GarageReviewStatus = (typeof GARAGE_REVIEW_STATUS)[number]

export const VOERTUIG_REMINDER_SOORT = ['KLEINE_BEURT', 'GROTE_BEURT', 'APK', 'BANDEN'] as const
export type VoertuigReminderSoort = (typeof VOERTUIG_REMINDER_SOORT)[number]

export const VOERTUIG_REMINDER_SOORT_LABELS: Record<VoertuigReminderSoort, string> = {
  KLEINE_BEURT: 'Kleine beurt',
  GROTE_BEURT: 'Grote beurt',
  APK: 'APK',
  BANDEN: 'Bandenwissel',
}

export const VOERTUIG_AFSPRAAK_DOCUMENT_SOORT = [
  'FACTUUR',
  'PAKBON',
  'WERKORDER',
  'OVERIG',
  // batch-65 apk-rapport (FO-478) - gezet door de APK-rapport-registratie.
  'APK_RAPPORT',
] as const
export type VoertuigAfspraakDocumentSoort = (typeof VOERTUIG_AFSPRAAK_DOCUMENT_SOORT)[number]

/**
 * batch-65 apk-rapport (FO-478) - de soorten die je bij een afspraak zelf kiest
 * (upload en koppelen). APK_RAPPORT hoort daar bewust NIET bij: een APK-rapport
 * leg je vast als documenttype APK-rapport (met keuringsdatum, uitslag en
 * vervaldatum); die registratie koppelt het zelf aan de afspraak. Herkent het
 * automatisch inlezen van een garagedocument een keuringsrapport, dan wordt dat
 * rapport ook langs dat pad vastgelegd.
 */
export const VOERTUIG_AFSPRAAK_DOCUMENT_UPLOAD_SOORT = [
  'FACTUUR',
  'PAKBON',
  'WERKORDER',
  'OVERIG',
] as const satisfies readonly VoertuigAfspraakDocumentSoort[]
export type VoertuigAfspraakDocumentUploadSoort =
  (typeof VOERTUIG_AFSPRAAK_DOCUMENT_UPLOAD_SOORT)[number]

export const VOERTUIG_AFSPRAAK_DOCUMENT_SOORT_LABELS: Record<VoertuigAfspraakDocumentSoort, string> =
  {
    FACTUUR: 'Factuur',
    PAKBON: 'Pakbon',
    WERKORDER: 'Werkorder',
    OVERIG: 'Overig',
    APK_RAPPORT: 'APK-rapport',
  }

/**
 * Welke afspraak-soort dekt een reminder? Een APK-reminder wordt gedekt door een
 * APK-afspraak, de beurten en de bandenwissel door een ONDERHOUD-afspraak -
 * dezelfde dekkingsregel als de FO-435-actie-status.
 */
export function afspraakSoortVoorReminder(soort: VoertuigReminderSoort): VoertuigAfspraakSoort {
  return soort === 'APK' ? 'APK' : 'ONDERHOUD'
}

// ─── Interval-instellingen (FO-465 §C.6e) ────────────────────────────────────

export interface VoertuigOnderhoudIntervalWaarden {
  /** Datum-interval in maanden (null = geen datum-drempel). */
  maanden: number | null
  /** Km-interval (null = geen km-drempel). */
  km: number | null
  /** Zoveel dagen vóór de vervaldatum wordt de reminder "actie vereist". */
  drempelDagen: number
  /** Zoveel km vóór de km-drempel wordt de reminder "actie vereist". */
  drempelKm: number
  actief: boolean
}

/**
 * Standaard-intervallen (beheerbaar per soort). Datum EN km-drempel, wat het
 * eerst komt. Banden = seizoenswissel (half jaar), APK = jaarlijks.
 */
export const VOERTUIG_ONDERHOUD_INTERVAL_DEFAULTS: Record<
  VoertuigReminderSoort,
  VoertuigOnderhoudIntervalWaarden
> = {
  KLEINE_BEURT: { maanden: 12, km: 20_000, drempelDagen: 30, drempelKm: 1_500, actief: true },
  GROTE_BEURT: { maanden: 24, km: 40_000, drempelDagen: 30, drempelKm: 2_000, actief: true },
  APK: { maanden: 12, km: null, drempelDagen: 30, drempelKm: 0, actief: true },
  BANDEN: { maanden: 6, km: null, drempelDagen: 21, drempelKm: 0, actief: true },
}

export interface VoertuigOnderhoudIntervalDTO extends VoertuigOnderhoudIntervalWaarden {
  soort: VoertuigReminderSoort
  soortLabel: string
  /** ISO-tijdstip van de laatste wijziging (null = standaardwaarde, nooit opgeslagen). */
  bewerktOp: string | null
}

const intervalRegelSchema = z
  .object({
    soort: z.enum(VOERTUIG_REMINDER_SOORT),
    maanden: z.number().int().min(1, 'Minimaal 1 maand').max(120, 'Maximaal 120 maanden').nullable(),
    km: z.number().int().min(500, 'Minimaal 500 km').max(500_000, 'Maximaal 500.000 km').nullable(),
    drempelDagen: z
      .number({ message: 'Vul een aantal dagen in' })
      .int('Gebruik een heel aantal dagen')
      .min(0, 'Minimaal 0 dagen')
      .max(365, 'Maximaal 365 dagen'),
    drempelKm: z
      .number({ message: 'Vul een aantal km in' })
      .int('Gebruik een heel aantal km')
      .min(0, 'Minimaal 0 km')
      .max(50_000, 'Maximaal 50.000 km'),
    actief: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.actief && v.maanden === null && v.km === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['maanden'],
        message: 'Vul een interval in maanden of km in, of zet deze soort uit.',
      })
    }
  })

export const voertuigOnderhoudIntervallenSchema = z.object({
  intervallen: z
    .array(intervalRegelSchema)
    .min(1, 'Vul minimaal één soort in')
    .max(VOERTUIG_REMINDER_SOORT.length, 'Elke soort mag maar één keer voorkomen.')
    .superRefine((lijst, ctx) => {
      const gezien = new Set<string>()
      lijst.forEach((r, i) => {
        if (gezien.has(r.soort)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'soort'],
            message: 'Elke soort mag maar één keer voorkomen.',
          })
        }
        gezien.add(r.soort)
      })
    }),
})
export type VoertuigOnderhoudIntervallenInput = z.infer<typeof voertuigOnderhoudIntervallenSchema>

// ─── Gedeelde bouwstenen ─────────────────────────────────────────────────────

const isoDagSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een geldige datum (dd-mm-jjjj)')

const tijdSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik een tijd in de vorm uu:mm')

/** Reden bij een correctie (herclassificatie, schade-toewijzing, km terugdraaien). */
export const garageCorrectieRedenSchema = z
  .string()
  .trim()
  .min(3, 'Geef een reden op (minimaal 3 tekens)')
  .max(1000, 'Maximaal 1000 tekens')

// ─── FO-462: kostencategorie per regel ───────────────────────────────────────

/**
 * Categorie van één garagefactuurregel kiezen of corrigeren. De reden is
 * VERPLICHT zodra een al ingedeelde regel een andere categorie krijgt
 * (herclassificatie); bij het invullen van een open keuze mag hij leeg. Dat
 * hangt van de huidige stand af, dus de API dwingt het af (veldfout op
 * `reden`), het schema staat een lege reden toe.
 */
export const garageRegelCategorieSchema = z.object({
  kostenCategorie: z.enum(GARAGE_KOSTEN_CATEGORIE),
  reden: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
})
export type GarageRegelCategorieInput = z.infer<typeof garageRegelCategorieSchema>

/** Meerdere regels van één factuur in één handeling (de review-keuze). */
export const garageRegelCategorieBulkSchema = z.object({
  regels: z
    .array(
      z.object({
        regelId: z.string().min(1),
        kostenCategorie: z.enum(GARAGE_KOSTEN_CATEGORIE),
      }),
    )
    .min(1, 'Kies voor minstens één regel een categorie')
    .max(200),
  reden: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
})
export type GarageRegelCategorieBulkInput = z.infer<typeof garageRegelCategorieBulkSchema>

// ─── FO-462: schade-toewijzing (berijder + dossier) ──────────────────────────

export const garageSchadeToewijzingSchema = z
  .object({
    /** De berijder; null = bevestigd "niet toewijsbaar" (medewerker blijft leeg). */
    employeeId: z.string().min(1).nullable(),
    /** Schadedatum (yyyy-mm-dd); weglaten = ongewijzigd. */
    schadeDatum: isoDagSchema.nullable().optional(),
    /** Bestaand schadedossier (SchadeMelding); null = losmaken. */
    schadeMeldingId: z.string().min(1).nullable().optional(),
    /** Maak een nieuw schadedossier aan (vereist een berijder). */
    maakDossier: z.boolean().optional(),
    reden: garageCorrectieRedenSchema,
  })
  .superRefine((v, ctx) => {
    if (v.maakDossier && !v.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employeeId'],
        message: 'Kies de berijder: zonder berijder kan er geen schadedossier worden aangemaakt.',
      })
    }
    if (v.maakDossier && v.schadeMeldingId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['schadeMeldingId'],
        message: 'Koppel aan een bestaand dossier of maak een nieuw aan, niet allebei.',
      })
    }
  })
export type GarageSchadeToewijzingInput = z.infer<typeof garageSchadeToewijzingSchema>

// ─── FO-465: review-punten afhandelen ────────────────────────────────────────

export const GARAGE_REVIEW_AFHANDELING = [
  /** Km-stand van de factuur toch registreren (bewuste correctie). */
  'KM_OVERNEMEN',
  /** Een andere (juiste) km-stand registreren. */
  'KM_CORRIGEREN',
  /** Werkdatum op de onderhoudsregel corrigeren. */
  'DATUM_CORRIGEREN',
  /** Afwijking gezien en akkoord (bv. late facturatie). */
  'BEVESTIGEN',
  /** Niets registreren (met toelichting). */
  'NEGEREN',
] as const
export type GarageReviewAfhandeling = (typeof GARAGE_REVIEW_AFHANDELING)[number]

/**
 * Labels van de afhandeling op een review-punt: de keuzes hierboven plus de
 * codes waarmee het systeem een punt vanzelf sluit (vanuit het garagepaneel).
 */
export const GARAGE_REVIEW_AFHANDELING_LABELS: Record<
  | GarageReviewAfhandeling
  | 'INGEDEELD'
  | 'BEVESTIGD'
  | 'GEEN_SCHADE_MEER'
  | 'HERBEPAALD'
  | 'FACTUUR_GEANNULEERD',
  string
> = {
  HERBEPAALD: 'Opnieuw bepaald na een gecorrigeerde werkdatum',
  FACTUUR_GEANNULEERD: 'Factuur geannuleerd',
  KM_OVERNEMEN: 'Stand van de factuur geregistreerd',
  KM_CORRIGEREN: 'Andere stand geregistreerd',
  DATUM_CORRIGEREN: 'Werkdatum gecorrigeerd',
  BEVESTIGEN: 'Afwijking bevestigd',
  NEGEREN: 'Niets geregistreerd',
  INGEDEELD: 'Regels ingedeeld',
  BEVESTIGD: 'Berijder en dossier bevestigd',
  GEEN_SCHADE_MEER: 'Geen schade meer na herclassificatie',
}

export const garageReviewAfhandelSchema = z
  .object({
    afhandeling: z.enum(GARAGE_REVIEW_AFHANDELING),
    km: z
      .number({ message: 'Vul de kilometerstand in' })
      .int('Gebruik een hele kilometerstand')
      .min(0, 'De kilometerstand kan niet negatief zijn')
      .max(2_000_000, 'Maximaal 2.000.000 km')
      .optional(),
    datum: isoDagSchema.optional(),
    notitie: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.afhandeling === 'KM_CORRIGEREN' && v.km === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['km'],
        message: 'Vul de juiste kilometerstand in.',
      })
    }
    if (v.afhandeling === 'DATUM_CORRIGEREN' && !v.datum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['datum'],
        message: 'Vul de juiste werkdatum in.',
      })
    }
    if (v.afhandeling === 'NEGEREN' && (v.notitie?.trim().length ?? 0) < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Licht kort toe waarom er niets geregistreerd wordt.',
      })
    }
  })
export type GarageReviewAfhandelInput = z.infer<typeof garageReviewAfhandelSchema>

// ─── FO-465: planbord-ingang ─────────────────────────────────────────────────

/**
 * "Voertuig naar garage" vanaf het planbord: één afspraak (dezelfde
 * registratie als vanuit het voertuigdossier) + een dagregel voor de berijder
 * die wegbrengt. De reden is verplicht (soort + korte omschrijving).
 */
export const planbordVoertuigAfspraakSchema = z
  .object({
    voertuigId: z.string().min(1, 'Kies het voertuig'),
    employeeId: z.string().min(1, 'Kies de berijder die wegbrengt'),
    datum: isoDagSchema,
    startTijd: tijdSchema.default('08:00'),
    eindTijd: tijdSchema.default('10:00'),
    soort: z.enum(VOERTUIG_AFSPRAAK_SOORT),
    omschrijving: afspraakRedenSchema,
    garageClientId: z.string().min(1).nullable().optional(),
    garageNaam: z.string().trim().max(200, 'Maximaal 200 tekens').nullable().optional(),
    /** Doorzetten ondanks planning-conflicten (dubbele inplanning/ziekte/training). */
    force: z.boolean().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.eindTijd <= v.startTijd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eindTijd'],
        message: 'De eindtijd moet na de starttijd liggen.',
      })
    }
  })
export type PlanbordVoertuigAfspraakInput = z.infer<typeof planbordVoertuigAfspraakSchema>

// ─── FO-465 §B: documenten bij een afspraak ──────────────────────────────────

export const voertuigAfspraakDocumentKoppelSchema = z.object({
  documentId: z.string().min(1, 'Kies of upload eerst het document'),
  // batch-65 apk-rapport (FO-478) - APK_RAPPORT is geen handmatige keuze.
  soort: z.enum(VOERTUIG_AFSPRAAK_DOCUMENT_UPLOAD_SOORT).default('FACTUUR'),
})
export type VoertuigAfspraakDocumentKoppelInput = z.infer<
  typeof voertuigAfspraakDocumentKoppelSchema
>

/**
 * De automatisch ingelezen waarden van een document op de onderhoudsregel zetten.
 * De gebruiker ziet het voorstel en mag elke waarde bijstellen; wat hij
 * ongewijzigd overneemt wordt gemarkeerd als "uit factuur".
 */
export const voertuigAfspraakDocumentToepassenSchema = z.object({
  werkDatum: isoDagSchema.nullable().optional(),
  kmStand: z
    .number({ message: 'Vul de kilometerstand in' })
    .int('Gebruik een hele kilometerstand')
    .min(0, 'De kilometerstand kan niet negatief zijn')
    .max(2_000_000, 'Maximaal 2.000.000 km')
    .nullable()
    .optional(),
  apkVervaldatum: isoDagSchema.nullable().optional(),
  beschrijving: z.string().trim().max(2000, 'Maximaal 2000 tekens').nullable().optional(),
  /**
   * Een lagere of onrealistische km-stand wordt pas vastgelegd als de gebruiker
   * hem bewust bevestigt (spec 165 §5.2: nooit stil overschrijven).
   */
  bevestigKm: z.boolean().optional(),
})
export type VoertuigAfspraakDocumentToepassenInput = z.infer<
  typeof voertuigAfspraakDocumentToepassenSchema
>

export const voertuigAfspraakWegbrengTaakSchema = z.object({
  taakId: z.string().min(1),
})
export type VoertuigAfspraakWegbrengTaakInput = z.infer<typeof voertuigAfspraakWegbrengTaakSchema>

// ─── FO-465 §D: km-registratie terugdraaien ──────────────────────────────────

export const kmRegistratieTerugdraaienSchema = z.object({
  reden: garageCorrectieRedenSchema,
})
export type KmRegistratieTerugdraaienInput = z.infer<typeof kmRegistratieTerugdraaienSchema>

// ─── DTO's: factuur-paneel + review ──────────────────────────────────────────

export interface GarageRegelKoppelingDTO {
  id: string
  regelId: string
  positie: number
  omschrijving: string
  /** Voertuig-aandeel (excl. btw) volgens de FO-320-toewijzing; null = geen toewijzing. */
  bedrag: number | null
  classificatie: OnderhoudRegelClassificatie
  voorstelCategorie: GarageKostenCategorie | null
  voorstelZeker: boolean
  /** De geldende categorie; null = keuze vereist. */
  kostenCategorie: GarageKostenCategorie | null
  categorieBron: GarageKostenCategorieBron
  correctieReden: string | null
  gecorrigeerdDoorNaam: string | null
  gecorrigeerdOp: string | null
}

export interface GarageSchadeMeldingRefDTO {
  id: string
  meldingNummer: string
  /** ISO-dag van de schade (datumTijd, NL-kalenderdag). */
  datum: string
  bron: SchadeMeldingBron
}

export interface GarageSchadeKoppelingDTO {
  schadeDatum: string | null
  schadeDatumBron: GarageSchadeDatumBron | null
  employeeId: string | null
  employeeNaam: string | null
  toewijzing: GarageSchadeToewijzing | null
  nietToewijsbaarReden: string | null
  schadeMelding: GarageSchadeMeldingRefDTO | null
  /** Schademeldingen van dit voertuig rond de schadedatum (keuzelijst in de review). */
  kandidaten: GarageSchadeMeldingRefDTO[]
}

export interface GarageKostenSplitsingDTO {
  onderhoud: number
  schadeReparatie: number
  nogInTeDelen: number
}

export interface GarageReviewDetailsDTO {
  factuurKm?: number | null
  vorigeKm?: number | null
  vorigeDatum?: string | null
  volgendeKm?: number | null
  volgendeDatum?: string | null
  werkDatum?: string | null
  factuurdatum?: string | null
  regelIds?: string[]
  toelichting?: string | null
}

export interface GarageReviewItemDTO {
  id: string
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  /** ISO-dag. */
  factuurdatum: string
  kenteken: string
  voertuigId: string | null
  voertuigKenteken: string | null
  onderhoudId: string | null
  koppelingId: string | null
  soort: GarageReviewSoort
  soortLabel: string
  status: GarageReviewStatus
  reden: string
  details: GarageReviewDetailsDTO | null
  afhandeling: string | null
  afhandelNotitie: string | null
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  createdAt: string
}

export interface GarageFactuurSetDTO {
  koppelingId: string
  voertuigId: string
  kenteken: string
  merkModel: string
  onderhoudId: string
  rol: 'BRON' | 'DEELFACTUUR' | 'CREDITNOTA'
  /** ISO-dag (werkdatum, leidend boven de factuurdatum). */
  werkDatum: string
  kmStand: number | null
  regels: GarageRegelKoppelingDTO[]
  totalen: GarageKostenSplitsingDTO
  /** Null = geen schade-/reparatieregels in deze set. */
  schade: GarageSchadeKoppelingDTO | null
  afspraak: {
    id: string
    soort: VoertuigAfspraakSoort
    status: VoertuigAfspraakStatus
    afspraakDatum: string | null
  } | null
  reviewItems: GarageReviewItemDTO[]
}

export interface GarageFactuurPaneelDTO {
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  isWerkplaats: boolean
  sets: GarageFactuurSetDTO[]
  /** Open werkvoorraad-items (niet-matchbare sets) van deze factuur. */
  werkvoorraad: Array<{ id: string; kenteken: string; status: string; reden: string }>
  totalen: GarageKostenSplitsingDTO
  openReviewItems: number
}

// ─── DTO's: inzicht (FO-462 §C) ──────────────────────────────────────────────

export interface GarageKostenBakWaardeDTO {
  bedrag: number
  /** Aantal onderhoudsregels/schadegevallen (telt ook garantie/coulance met bedrag 0). */
  aantal: number
}

export interface GarageKostenSoortRegelDTO {
  /** Fijne classificatie; 'HANDMATIG' = handmatige/import-kosten zonder factuurregel. */
  soort: OnderhoudRegelClassificatie | 'HANDMATIG'
  soortLabel: string
  bak: GarageKostenBak
  bedrag: number
  aantal: number
}

export interface GarageKostenJaarDTO {
  jaar: number
  onderhoud: GarageKostenBakWaardeDTO
  schadeReparatie: GarageKostenBakWaardeDTO
  nogInTeDelen: GarageKostenBakWaardeDTO
  perSoort: GarageKostenSoortRegelDTO[]
  /** Verreden km in dat jaar (km-registraties); null = te weinig standen. */
  kmVerreden: number | null
  onderhoudPerKm: number | null
  schadePerKm: number | null
}

export interface GarageSchadeGevalDTO {
  /** Dossier-id (SchadeMelding) of - zonder dossier - de factuurkoppeling-id. */
  sleutel: string
  schadeMelding: GarageSchadeMeldingRefDTO | null
  schadeDatum: string | null
  voertuigId: string
  kenteken: string
  employeeId: string | null
  employeeNaam: string | null
  toewijzing: GarageSchadeToewijzing | null
  nietToewijsbaarReden: string | null
  facturen: Array<{ factuurId: string; factuurNr: string; bedrag: number }>
  bedrag: number
}

export interface VoertuigGarageKostenDTO {
  voertuigId: string
  kenteken: string
  /** ISO-dagen van de periode. */
  van: string
  tot: string
  jaren: GarageKostenJaarDTO[]
  totaal: GarageKostenSplitsingDTO
  schades: GarageSchadeGevalDTO[]
}

export interface MedewerkerGarageSchadeDTO {
  employeeId: string
  van: string
  tot: string
  aantalSchades: number
  totaalBedrag: number
  perJaar: Array<{ jaar: number; aantal: number; bedrag: number }>
  schades: GarageSchadeGevalDTO[]
}

/**
 * Maand-aggregatie voor de kostprijs-kant (FO-460/461, stream kostprijs-werkelijk
 * consumeert dit; integrator wist beide aan elkaar). Bedragen als string met twee
 * decimalen (Decimal-precisie, nooit een float-afronding onderweg).
 */
export interface GarageKostenMaandRegelDTO {
  /** yyyy-MM */
  maand: string
  voertuigId: string
  /** Schade-kosten: de berijder op de schadedatum (null = niet toewijsbaar). */
  employeeId: string | null
  bak: GarageKostenBak
  bedrag: string
  aantal: number
}

// ─── DTO's: reminders + keten (FO-465) ───────────────────────────────────────

export const VOERTUIG_REMINDER_STATUS = ['ACTIE_VEREIST', 'GEPLAND', 'OK'] as const
export type VoertuigReminderStatus = (typeof VOERTUIG_REMINDER_STATUS)[number]

export const VOERTUIG_REMINDER_STATUS_LABELS: Record<VoertuigReminderStatus, string> = {
  ACTIE_VEREIST: 'Actie vereist',
  GEPLAND: 'Gepland',
  OK: 'Op schema',
}

export interface VoertuigOnderhoudReminderDTO {
  id: string
  voertuigId: string
  soort: VoertuigReminderSoort
  soortLabel: string
  /** ISO-dag van de laatste uitvoering. */
  basisDatum: string
  basisKm: number | null
  vervalDatum: string | null
  vervalKm: number | null
  geschatteKmDatum: string | null
  /** De eerste van beide drempels. */
  effectieveDatum: string | null
  bepalend: 'DATUM' | 'KM' | null
  status: VoertuigReminderStatus
  statusLabel: string
  dagenTot: number | null
  kmTot: number | null
  afspraakId: string | null
  taakId: string | null
  /**
   * batch-68 werkplaatsfacturen (FO-341 §3.4) - waar het interval van dit voertuig
   * vandaan komt (override, merk+model, merk of standaard). Optioneel.
   */
  intervalBron?: 'VOERTUIG' | 'MERK_MODEL' | 'MERK' | 'STANDAARD'
  /** batch-68 (FO-341) - APK gefactureerd, wacht op de RDW (alleen bij soort APK). */
  apkVoorlopig?: boolean
}

export interface VoertuigAfspraakDocumentExtractieDTO {
  kenteken: string | null
  werkDatum: string | null
  kmStand: number | null
  apkVervaldatum: string | null
  regels: Array<{
    omschrijving: string
    bedrag: number | null
    classificatie: OnderhoudRegelClassificatie
  }>
  /**
   * batch-65 apk-rapport (FO-478 §3.E.11) - een in dit document herkend APK-
   * keuringsrapport: vastgelegd als APK-rapport (documentId), of met de reden
   * waarom niet. null = geen keuringsrapport herkend.
   */
  keuringsrapport: { vastgelegd: boolean; documentId: string | null; melding: string } | null
}

export interface VoertuigAfspraakDocumentDTO {
  id: string
  documentId: string
  /** Laatste versie (voor de same-origin-downloadroute); null = geen versie. */
  versionNr: number | null
  fileName: string | null
  soort: VoertuigAfspraakDocumentSoort
  extractieStatus: 'OK' | 'FOUT' | 'GEEN' | null
  extractieFout: string | null
  extractie: VoertuigAfspraakDocumentExtractieDTO | null
  toegepastOp: string | null
  createdAt: string
}

/** Factuur op de keten van een afspraak (download via de inkoop-PDF-proxy). */
export interface VoertuigAfspraakFactuurDTO {
  factuurId: string
  factuurNr: string
  rol: 'BRON' | 'DEELFACTUUR' | 'CREDITNOTA'
  /** Voertuig-aandeel van deze factuur; null = geen kosten-inzage. */
  bedrag: number | null
}

/** Kandidaat-inkoopfactuur om aan een afspraak te koppelen. */
export interface GarageKoppelbareFactuurDTO {
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  /** ISO-dag. */
  factuurdatum: string
  /** Factuurtotaal excl. btw; null = geen kosten-inzage. */
  bedrag: number | null
  isWerkplaats: boolean
  /** Staat het kenteken van dit voertuig op de factuur? */
  kentekenOpFactuur: boolean
}
